/**
 * מזהה יעד פנימי (internal_hotel_destinations.id) לשימוש ב־GET /api/attractions/search.
 * חיפוש אטרקציות: עדיף internal_destination_id (מ־/api/destinations/resolve) = public.internal_hotel_destinations.id.
 * fallback זמני: destination_id מספרי בלבד (legacy).
 */

function asPositiveInt(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

function fromObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = [
    "internal_destination_id",
    "InternalDestinationId",
    "internalDestinationId",
    "destination_id",
  ];
  for (const k of keys) {
    const id = asPositiveInt(obj[k]);
    if (id != null) return id;
  }
  const nested = [
    obj.merge_meta,
    obj.internal_inventory_meta,
    obj.internalMeta,
    obj.internal_meta,
  ];
  for (const n of nested) {
    if (n && typeof n === "object") {
      const id = asPositiveInt(
        n.internal_destination_id ?? n.destination_id ?? n.DestinationId
      );
      if (id != null) return id;
    }
  }
  return null;
}

function fromHotelRecord(h) {
  if (!h || typeof h !== "object") return null;
  const direct = fromObject(h);
  if (direct != null) return direct;
  return (
    asPositiveInt(h.DestinationId) ||
    asPositiveInt(h.destinationId) ||
    null
  );
}

/** ערך זהה ל־DYNAMIC_PACKAGES מ־SearchContext (ללא ייבוא מעגלי). */
const TAB_DYNAMIC_PACKAGES = "DynamicPackages";

function rowDestinationCandidates(row, searchTab) {
  const list = [];
  if (row && typeof row === "object") {
    list.push(row);
    if (searchTab === TAB_DYNAMIC_PACKAGES) {
      const h = row.fullDeal?.hotel || row.hotel;
      if (h && typeof h === "object") list.push(h);
    }
  }
  return list;
}

/**
 * @param {{
 *   location?: unknown,
 *   searchPhrase?: unknown,
 *   hotels?: unknown[],
 *   searchTab?: string,
 * }} input
 * @returns {number | null}
 */
/** מחלץ קוד IATA מ־label (למשל "Athens (ATH)" או "נחיתה ב-ATH"). */
function iataCodeFromLabel(label) {
  if (!label || typeof label !== "string") return "";
  const paren = label.match(/\(([A-Za-z]{2,3})\)/);
  if (paren) {
    const c = paren[1].toUpperCase();
    if (c !== "ALL") return c;
  }
  const upper = label.toUpperCase();
  const words = upper.match(/\b[A-Z]{3}\b/g) || [];
  const skip = new Set([
    "THE",
    "AND",
    "FOR",
    "ARE",
    "BUT",
    "NOT",
    "YOU",
    "ALL",
    "ANY",
    "CAN",
    "DAY",
    "GET",
    "HAS",
    "HIM",
    "HIS",
    "HOW",
    "ITS",
    "LET",
    "MAY",
    "NEW",
    "NOW",
    "OLD",
    "OUR",
    "OUT",
    "PUT",
    "SAY",
    "SHE",
    "TOO",
    "TWO",
    "USE",
    "WHO",
    "BOY",
    "DID",
    "HER",
    "WAS",
    "ONE",
  ]);
  for (const w of words) {
    if (!skip.has(w)) return w;
  }
  return "";
}

function normalizeLdsToken(v) {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (!s) return "";
  const u = s.toUpperCase();
  if (u === "ALL") return "";
  if (/^[A-Z]{2,3}$/i.test(s)) return u;
  return "";
}

function ldsFromLocationValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return normalizeLdsToken(value);
  if (typeof value === "object") {
    return normalizeLdsToken(
      value.code ??
        value.AIRPORTCODE ??
        value.CITYCODE ??
        value.lds_code ??
        ""
    );
  }
  return "";
}

function nameFromLabel(label) {
  if (!label || typeof label !== "string") return "";
  const beforeParen = label.split("(")[0].trim();
  return beforeParen || label.trim();
}

/**
 * רמזים ל־GET /api/destinations/resolve כשאין destination_id מספרי בקונטקסט החיפוש.
 * @returns {{ lds_code: string, name: string }}
 */
function nameFromLocationValue(value) {
  if (value == null || typeof value !== "object") return "";
  return String(value.CITYNAME || value.city || value.name || "").trim();
}

export function getDestinationResolveHints(location, searchPhrase) {
  const sp = searchPhrase && typeof searchPhrase === "object" ? searchPhrase : null;
  const loc = location && typeof location === "object" ? location : null;

  const ldsFromPhraseRaw = sp?.AIRPORTCODE || sp?.CITYCODE || null;
  let lds_code = normalizeLdsToken(ldsFromPhraseRaw);

  /** שם ל־resolve — ללא CITYNAME_HEB; CITYNAME לפני city כדי להעדיף אנגלית כשקיים */
  let name = "";
  if (sp) {
    name = String(
      sp.CITYNAME ||
        sp.city ||
        (loc?.name != null ? loc.name : "") ||
        sp.userQuery ||
        ""
    ).trim();
  }

  if (loc) {
    if (!lds_code) {
      lds_code =
        normalizeLdsToken(loc.lds_code) ||
        normalizeLdsToken(loc.code) ||
        ldsFromLocationValue(loc.value) ||
        normalizeLdsToken(loc.AIRPORTCODE || loc.CITYCODE || "");
    }
    if (!lds_code && loc.label) {
      lds_code = iataCodeFromLabel(String(loc.label));
    }

    if (!name) {
      name =
        String(loc.CITYNAME || loc.city || loc.name || "").trim() ||
        nameFromLocationValue(loc.value);
      if (!name && loc.label) {
        name = nameFromLabel(String(loc.label));
      }
    }
  }

  if (sp && !lds_code) {
    lds_code =
      normalizeLdsToken(sp.lds_code) ||
      normalizeLdsToken(sp.AIRPORTCODE) ||
      normalizeLdsToken(sp.CITYCODE) ||
      normalizeLdsToken(sp.code) ||
      "";
  }

  return { lds_code, name };
}

export function resolveAttractionsDestinationId({
  location,
  searchPhrase,
  hotels,
  searchTab = "",
} = {}) {
  const fromLoc = fromObject(location);
  if (fromLoc != null) return fromLoc;

  const fromPhrase = fromObject(searchPhrase);
  if (fromPhrase != null) return fromPhrase;

  if (Array.isArray(hotels)) {
    for (const row of hotels) {
      for (const obj of rowDestinationCandidates(row, searchTab)) {
        const id = fromHotelRecord(obj);
        if (id != null) return id;
      }
    }
  }

  return null;
}

/**
 * קוד יעד/היקף טיסה (IATA, למשל ATH) ל־GET /api/attractions/rules — `flight_scope_value`.
 * סדר עדיפות: רמזי חיפוש → נחיתה בטיסה הלוך → תווית עיר במלון.
 * @returns {string | null}
 */
function flightScopeFromLinkedFlights(linkedFlights) {
  if (!linkedFlights || typeof linkedFlights !== "object") return "";
  const flight = linkedFlights.flight;
  const segments = Array.isArray(flight) ? flight : flight ? [flight] : [];
  const first = segments[0];
  if (!first) return "";
  const legsRaw = first.leg;
  const legs = Array.isArray(legsRaw) ? legsRaw : legsRaw ? [legsRaw] : [];
  const lastLeg = legs.length ? legs[legs.length - 1] : null;
  const raw = lastLeg?.arrAirport?.code || lastLeg?.arrCity || "";
  return normalizeLdsToken(raw);
}

export function resolveFlightScopeValueForPackageProfile({
  location,
  searchPhrase,
  selectedResult,
} = {}) {
  const hints = getDestinationResolveHints(location, searchPhrase);
  if (hints.lds_code) return hints.lds_code;

  const fromFlight = flightScopeFromLinkedFlights(selectedResult?.linkedFlights);
  if (fromFlight) return fromFlight;

  const labelish =
    selectedResult?.hotel?.hotelCity ||
    selectedResult?.hotel?.destination ||
    "";
  if (labelish && typeof labelish === "string") {
    const fromLabel = iataCodeFromLabel(labelish);
    if (fromLabel) return fromLabel;
  }

  return null;
}
